import { isString, isEmpty } from "lodash";
import { cachedToggles } from "./cached-toggles";
import "./opt-in-out/set-up.js";

export { useFeatureBubble } from "./opt-in-out/opt-in-out.hook";
export { featureBubble } from "./opt-in-out/opt-in-out.js";
export { featureBubbleDefinitions } from "./opt-in-out/feature-definitions";

const FLAGSMITH_READY_RETRY_MS = 2000;
const MAX_RETRY_ATTEMPTS = 10; // ~20s total — bail out if init never completes

// DEPRECATED: No longer needed - kept for backward compatibility
export function fetchFeatureToggles() {
  console.warn("fetchFeatureToggles is deprecated");
  return Promise.resolve({});
}

export function featureEnabled(...toggleNames: string[]) {
  if (
    toggleNames.length === 0 ||
    toggleNames.find((name) => !isString(name) || isEmpty(name))
  ) {
    throw new Error(
      `featureEnabled must be called with one or more non-empty strings representing the feature name(s)`
    );
  }

  const allOverrides = getAllLocalStorageFeatureToggles();

  // Check if ANY of the passed toggles are enabled
  return toggleNames.some((toggleName) => {
    // Check localStorage override first (for dev workflow)
    if (toggleName in allOverrides) {
      return allOverrides[toggleName];
    }

    if (typeof flagsmithInstance !== "undefined") {
      return flagsmithInstance.hasFeature(toggleName);
    }

    // SDK not ready — return the cached value (hydrated from window.__canopy_toggles
    // at module load) and defer the hasFeature tracking call until init resolves.
    scheduleHasFeatureTracking(toggleName);
    return cachedToggles[toggleName] ?? false;
  });
}

// Polls for flagsmithInstance and fires hasFeature() once it's available so the
// SDK's analytics event is still recorded for calls made before init resolved.
// Bounded by MAX_RETRY_ATTEMPTS so a never-resolving init doesn't poll forever.
function scheduleHasFeatureTracking(toggleName: string, attempt = 1) {
  if (attempt > MAX_RETRY_ATTEMPTS) return;
  setTimeout(() => {
    if (typeof flagsmithInstance !== "undefined") {
      flagsmithInstance.hasFeature(toggleName);
      return;
    }
    scheduleHasFeatureTracking(toggleName, attempt + 1);
  }, FLAGSMITH_READY_RETRY_MS);
}

export function getAllLocalStorageFeatureToggles() {
  const featureKeys = Object.keys(window.localStorage).filter((key) =>
    key.includes("feature:")
  );
  return featureKeys.reduce(
    (accumulator: Record<string, boolean>, currentValue) => {
      const newString = currentValue.split("feature:")[1];
      const localStorageValue = localStorage.getItem(currentValue);
      if (
        localStorageValue === "on" ||
        localStorageValue === "true" ||
        localStorageValue === "off" ||
        localStorageValue === "false"
      ) {
        accumulator[newString] =
          localStorageValue === "on" || localStorageValue === "true";
      }
      return accumulator;
    },
    {}
  );
}

export function getAllToggleInformationForSentry() {
  // Read from window at call time so Sentry context is current even if this
  // module was imported before flagsmithBootstrap populated __canopy_toggles.
  return { ...(window.__canopy_toggles || {}) };
}
